import React, { useEffect, useState } from 'react';
import {
    Box, Typography, Button, Grid, Card, CardContent, CardActions,
    CircularProgress, Alert, Dialog, DialogContent
} from '@mui/material';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import subscriptionService from '../../../services/subscriptionService';
import paymentService from '../../../services/paymentService';
import PaymentMethodModal from '../components/PaymentMethodModal';

const LockedModal = ({ alertMessage }) => {
    const [packages, setPackages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [balance, setBalance] = useState(0);
    const [openDeposit, setOpenDeposit] = useState(false);
    const [selectedPackage, setSelectedPackage] = useState(null);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            setLoading(true);
            const [pkgRes, balRes] = await Promise.all([
                subscriptionService.getPackages(),
                paymentService.getBalance()
            ]);

            setPackages(pkgRes.data || []);
            setBalance(Number(balRes.balance || 0));
        } catch (err) {
            console.error(err);
            setError('Không thể tải danh sách gói cước.');
        } finally {
            setLoading(false);
        }
    };

    const handleBuy = (pkg) => {
        setSelectedPackage(pkg);
        setOpenDeposit(true);
    };

    return (
        <Box
            sx={{
                width: '100%',
                minHeight: '80vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'transparent',
            }}
        >
            <Box
                sx={{
                    width: '100%',
                    maxWidth: '1600px',
                    mx: 'auto',
                    textAlign: 'center'
                }}
            >
                <Box textAlign="center" mb={6} mt={4}>
                    <Box
                        sx={{
                            display: 'inline-flex',
                            p: 2,
                            borderRadius: '50%',
                            bgcolor: 'rgba(59, 130, 246, 0.1)',
                            mb: 3
                        }}
                    >
                        <LockOpenIcon sx={{ fontSize: 48, color: '#60a5fa' }} />
                    </Box>
                    <Typography variant="h3" fontWeight="bold" gutterBottom sx={{ background: 'linear-gradient(45deg, #60a5fa 30%, #a78bfa 90%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                        Mở Khóa Tính Năng Cao Cấp
                    </Typography>
                    <Typography variant="h6" color="#94a3b8" maxWidth="600px" mx="auto">
                        Nâng cấp tài khoản để quản lý Host không giới hạn và truy cập các công cụ tự động hóa mạnh mẽ.
                    </Typography>
                </Box>

                {alertMessage && (
                    <Alert severity="warning" variant="filled" sx={{ mb: 4, mx: 'auto', maxWidth: 600, justifyContent: 'center' }}>
                        {alertMessage}
                    </Alert>
                )}

                {error && <Alert severity="error" sx={{ mb: 4, mx: 'auto', maxWidth: 600 }}>{error}</Alert>}

                {loading ? (
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight="30vh">
                        <CircularProgress sx={{ color: '#3b82f6' }} />
                    </Box>
                ) : (
                    <Grid container spacing={4} justifyContent="center" alignItems="stretch">
                        {packages.map((pkg) => (
                            <Grid item xs={12} sm={6} md={4} key={pkg.id}>
                                <Card
                                    sx={{
                                        height: '100%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        position: 'relative',
                                        bgcolor: 'transparent',
                                        backgroundImage: 'linear-gradient(rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0))',
                                        color: 'white',
                                        borderRadius: 4,
                                        border: '1px solid',
                                        borderColor: pkg.isPopular ? '#06b6d4' : 'rgba(255,255,255,0.1)',
                                        boxShadow: pkg.isPopular ? '0 0 15px rgba(6, 182, 212, 0.2)' : 'none',
                                        transition: 'all 0.3s ease',
                                        '&:hover': {
                                            transform: 'translateY(-5px)',
                                            boxShadow: '0 8px 25px rgba(0,0,0,0.5)',
                                            borderColor: '#06b6d4'
                                        }
                                    }}
                                >
                                    <CardContent sx={{ flexGrow: 1, p: 3, textAlign: 'center' }}>
                                        <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ color: '#cbd5e1', fontSize: '1.25rem' }}>
                                            {pkg.name}
                                        </Typography>

                                        <Box display="flex" alignItems="baseline" justifyContent="center" mb={0.5}>
                                            <Typography variant="h4" fontWeight="bold" sx={{ color: '#06b6d4' }}>
                                                {Number(pkg.price).toLocaleString()}
                                            </Typography>
                                            <Typography variant="body2" sx={{ color: '#06b6d4', ml: 0.5, fontWeight: 'bold' }}>VND</Typography>
                                        </Box>

                                        <Typography variant="caption" color="#64748b" mb={3} display="block">
                                            Thời hạn: {pkg.durationDays} ngày
                                        </Typography>

                                        <Box display="flex" flexDirection="column" gap={1.5} textAlign="left" sx={{ pl: 1 }}>
                                            <Box display="flex" alignItems="center">
                                                <CheckCircleIcon sx={{ fontSize: 18, color: '#22c55e', mr: 1 }} />
                                                <Typography variant="body2" color="#94a3b8" sx={{ fontSize: '0.9rem' }}>Quản lý Host Account</Typography>
                                            </Box>
                                            <Box display="flex" alignItems="center">
                                                <CheckCircleIcon sx={{ fontSize: 18, color: '#22c55e', mr: 1 }} />
                                                <Typography variant="body2" color="#94a3b8" sx={{ fontSize: '0.9rem' }}>Đồng bộ tự động</Typography>
                                            </Box>
                                            <Box display="flex" alignItems="center">
                                                <CheckCircleIcon sx={{ fontSize: 18, color: '#22c55e', mr: 1 }} />
                                                <Typography variant="body2" color="#94a3b8" sx={{ fontSize: '0.9rem' }}>Hỗ trợ 24/7</Typography>
                                            </Box>
                                            {(Array.isArray(pkg.features) ? pkg.features : (typeof pkg.features === 'string' ? JSON.parse(pkg.features || '[]') : [])).map((feat, idx) => (
                                                <Box key={idx} display="flex" alignItems="center">
                                                    <CheckCircleIcon sx={{ fontSize: 18, color: '#22c55e', mr: 1 }} />
                                                    <Typography variant="body2" color="#94a3b8" sx={{ fontSize: '0.9rem' }}>{feat}</Typography>
                                                </Box>
                                            ))}
                                        </Box>

                                        {pkg.description && (
                                            <Typography variant="caption" color="#64748b" display="block" mt={3} fontStyle="italic" sx={{ opacity: 0.8 }}>
                                                {pkg.description}
                                            </Typography>
                                        )}
                                    </CardContent>

                                    <CardActions sx={{ p: 3, pt: 0 }}>
                                        <Button
                                            fullWidth
                                            variant="outlined"
                                            size="medium"
                                            onClick={() => handleBuy(pkg)}
                                            sx={{
                                                borderColor: '#0f172a',
                                                bgcolor: 'rgba(15, 23, 42, 0.6)',
                                                color: '#06b6d4',
                                                fontWeight: 'bold',
                                                py: 1.5,
                                                borderRadius: 2,
                                                borderWidth: '1px',
                                                boxShadow: 'inset 0 0 0 1px rgba(6, 182, 212, 0.2)',
                                                '&:hover': {
                                                    bgcolor: 'rgba(6, 182, 212, 0.1)',
                                                    color: '#06b6d4',
                                                    borderColor: '#06b6d4',
                                                    boxShadow: 'inset 0 0 0 1px #06b6d4'
                                                }
                                            }}
                                        >
                                            Đăng Ký Ngay
                                        </Button>
                                    </CardActions>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                )}
            </Box>

            <PaymentMethodModal
                open={openDeposit}
                onClose={() => setOpenDeposit(false)}
                pkg={selectedPackage}
                currentBalance={balance}
                onSuccess={() => {
                    fetchData();
                    setOpenDeposit(false);
                    window.location.reload();
                }}
            />
        </Box>
    );
};

export default LockedModal;
